/**
 * Checklist Template Repository Implementation
 * Implements the ChecklistTemplateRepository interface
 * Coordinates between API calls and mappers
 */

import { ChecklistTemplateRepository } from "../../domain/repositories/ChecklistTemplateRepository";
import {
  ChecklistTemplateEntity,
  ChecklistTemplateItemEntity,
} from "../../domain/entities/ChecklistTemplateEntity";
import {
  getTemplate,
  updateTemplate,
  addTemplateItem as addTemplateItemApi,
  updateTemplateItem as updateTemplateItemApi,
  deleteTemplateItem,
} from "../api/checklistTemplateApi";
import { ChecklistTemplateMapper } from "../mappers/ChecklistTemplateMapper";
import {
  TemplateResponseDTO,
  UpdateTemplateRequestDTO,
  ChecklistTemplateItemDTO,
  AddTemplateItemRequestDTO,
  TemplateItemAddResponseDTO,
  UpdateSingleTemplateItemRequestDTO,
  TemplateItemUpdateResponseDTO,
} from "../models/ChecklistTemplateDTOs";

export class ChecklistTemplateRepositoryImpl
  implements ChecklistTemplateRepository
{
  async getTemplate(): Promise<ChecklistTemplateEntity | null> {
    const response = (await getTemplate()) as TemplateResponseDTO;
    return ChecklistTemplateMapper.toTemplateEntityFromResponse(response);
  }

  async getTemplateItems(
    templateId: number
  ): Promise<ChecklistTemplateItemEntity[]> {
    // Get template which now includes items
    const response = (await getTemplate()) as TemplateResponseDTO;
    if (!response.data || !response.data.items) {
      return [];
    }

    // Handle both array (legacy) and grouped by area format
    if (Array.isArray(response.data.items)) {
      return ChecklistTemplateMapper.toTemplateItemEntityArray(
        response.data.items
      );
    } else {
      // Items are grouped by accounting area - flatten them
      const allItems: ChecklistTemplateItemDTO[] = [];
      Object.values(response.data.items).forEach((areaItems) => {
        allItems.push(...areaItems);
      });
      return ChecklistTemplateMapper.toTemplateItemEntityArray(allItems);
    }
  }

  /**
   * Get template items grouped by accounting area
   */
  async getTemplateItemsGrouped(
    templateId: number
  ): Promise<Record<string, ChecklistTemplateItemEntity[]>> {
    const response = (await getTemplate()) as TemplateResponseDTO;
    if (!response.data || !response.data.items) {
      return {};
    }

    // If already grouped, map each area's items
    if (!Array.isArray(response.data.items)) {
      const grouped: Record<string, ChecklistTemplateItemEntity[]> = {};
      Object.entries(response.data.items).forEach(([area, items]) => {
        grouped[area] =
          ChecklistTemplateMapper.toTemplateItemEntityArray(items);
      });
      return grouped;
    }

    // If array format, group by accounting area
    const items = ChecklistTemplateMapper.toTemplateItemEntityArray(
      response.data.items
    );
    const grouped: Record<string, ChecklistTemplateItemEntity[]> = {};
    items.forEach((item) => {
      if (!grouped[item.accounting_area]) {
        grouped[item.accounting_area] = [];
      }
      grouped[item.accounting_area].push(item);
    });
    return grouped;
  }

  async updateTemplate(
    templateId: number,
    data: Partial<ChecklistTemplateEntity> & {
      items?: ChecklistTemplateItemEntity[];
    }
  ): Promise<ChecklistTemplateEntity> {
    const updateData: UpdateTemplateRequestDTO = {};

    if (data.items) {
      updateData.items = data.items.map((item) => ({
        id: item.id, // Include ID for existing items
        accounting_area: item.accounting_area,
        title: item.title,
        description: item.description,
        task_guidance: item.task_guidance,
        accepted_proof_types: item.accepted_proof_types,
        display_order: item.display_order,
        priority: item.priority,
        status: item.status,
      }));
    }

    if (data.name !== undefined) updateData.name = data.name;
    if (data.description !== undefined)
      updateData.description = data.description;
    if (data.is_active !== undefined) updateData.is_active = data.is_active;

    const response = (await updateTemplate(updateData)) as TemplateResponseDTO;
    const updatedTemplate =
      ChecklistTemplateMapper.toTemplateEntityFromResponse(response);
    if (!updatedTemplate) {
      throw new Error("Template not found");
    }
    return updatedTemplate;
  }

  /**
   * Update template and return both template and items from response
   * This is used internally to avoid refetching items after update
   */
  async updateTemplateWithItemsResponse(
    templateId: number,
    data: Partial<ChecklistTemplateEntity> & {
      items?: ChecklistTemplateItemEntity[];
    }
  ): Promise<{
    template: ChecklistTemplateEntity;
    items: ChecklistTemplateItemEntity[];
    itemsByArea: Record<string, ChecklistTemplateItemEntity[]>;
  }> {
    const updateData: UpdateTemplateRequestDTO = {};

    if (data.items) {
      updateData.items = data.items.map((item) => ({
        id: item.id,
        accounting_area: item.accounting_area,
        title: item.title,
        description: item.description,
        task_guidance: item.task_guidance,
        accepted_proof_types: item.accepted_proof_types,
        display_order: item.display_order,
        priority: item.priority,
        status: item.status,
      }));
    }

    if (data.name !== undefined) updateData.name = data.name;
    if (data.description !== undefined)
      updateData.description = data.description;
    if (data.is_active !== undefined) updateData.is_active = data.is_active;

    const response = (await updateTemplate(updateData)) as TemplateResponseDTO;
    const updatedTemplate =
      ChecklistTemplateMapper.toTemplateEntityFromResponse(response);
    if (!updatedTemplate) {
      throw new Error("Template not found");
    }

    // Extract items from response
    let itemsList: ChecklistTemplateItemEntity[] = [];
    let groupedItems: Record<string, ChecklistTemplateItemEntity[]> = {};

    if (response.data && response.data.items) {
      if (Array.isArray(response.data.items)) {
        itemsList = ChecklistTemplateMapper.toTemplateItemEntityArray(
          response.data.items
        );
        itemsList.forEach((item) => {
          if (!groupedItems[item.accounting_area]) {
            groupedItems[item.accounting_area] = [];
          }
          groupedItems[item.accounting_area].push(item);
        });
      } else {
        const allItems: any[] = [];
        Object.values(response.data.items).forEach((areaItems) => {
          allItems.push(...areaItems);
        });
        itemsList = ChecklistTemplateMapper.toTemplateItemEntityArray(allItems);
        Object.entries(response.data.items).forEach(([area, items]) => {
          groupedItems[area] =
            ChecklistTemplateMapper.toTemplateItemEntityArray(items as any[]);
        });
      }
    }

    return {
      template: updatedTemplate,
      items: itemsList,
      itemsByArea: groupedItems,
    };
  }

  async deleteTemplateItem(itemId: number, deleteFromCurrentMonth: boolean = false): Promise<void> {
    await deleteTemplateItem(itemId, deleteFromCurrentMonth);
  }

  async addTemplateItem(
    payload: AddTemplateItemRequestDTO
  ): Promise<{
    item_id: number;
    added_to_current_month_checklist: boolean;
  }> {
    const response = (await addTemplateItemApi(
      payload
    )) as TemplateItemAddResponseDTO;
    if (!response.data?.item_id) {
      throw new Error("Add template item: invalid response");
    }
    return response.data;
  }

  async updateTemplateItem(
    itemId: number,
    payload: UpdateSingleTemplateItemRequestDTO
  ): Promise<{
    current_month_sync: {
      applied: boolean;
      skipped_reason: string | null;
    };
  }> {
    const response = (await updateTemplateItemApi(
      itemId,
      payload
    )) as TemplateItemUpdateResponseDTO;
    if (!response.data?.current_month_sync) {
      throw new Error("Update template item: invalid response");
    }
    return response.data;
  }
}
